import { GetFieldPropertiesResult } from '@indigohive/cogfy-types/endpoints/getFieldProperties'
import { FieldOperationConditionOperator, FieldOperationOperatorCondition, FieldType, UUID } from '@indigohive/cogfy-types'
import { useTranslation } from 'react-i18next'
import { Select } from '../../Select'
import { FieldPropertyMention } from '../../FieldPropertyMention'
import { Button } from '../../Button'
import { TrashIcon } from 'lucide-react'

export type OperationConditionProps = {
  condition: FieldOperationOperatorCondition
  fields: { id: UUID, name: string, type: FieldType }[]
  inputOptions: { label: string, value: string }[]
  fieldProperties: GetFieldPropertiesResult
  operatorOptions: (fieldId?: UUID, inputProperty?: string) => {
    value: FieldOperationConditionOperator
    label: string
  }[]
  onChange?: (condition: FieldOperationOperatorCondition) => void
  onDelete?: () => void
}

export function OperationCondition (props: OperationConditionProps) {
  const {
    condition,
    fields,
    inputOptions,
    fieldProperties,
    operatorOptions,
    onChange,
    onDelete
  } = props

  const { t } = useTranslation()

  const isEmptyOperator = condition.operator === 'is_empty' || condition.operator === 'is_not_empty'
  const isErrorOperator = condition.operator === 'has_error' || condition.operator === 'has_no_error'

  return (
    <>
      <Select
        size="md"
        options={[
          { label: t('Select a field'), value: '' },
          ...inputOptions
        ]}
        value={condition.input?.fieldId + ':' + condition.input?.property}
        onChange={event => {
          const split = event.target.value.split(':')
          const property = split[1] ?? 'value'
          const fieldId = split[0] as UUID
          const fieldType = fields.find(field => field.id === split[0])?.type

          if (!fieldType) return

          onChange?.({
            input: { fieldId, property, fieldType },
            operator: null
          })
        }}
      />

      <Select
        size="md"
        options={[
          { label: t('Select a operator'), value: '' },
          ...operatorOptions(condition.input?.fieldId, condition.input?.property)
        ]}
        value={condition.operator ?? ''}
        onChange={event => {
          onChange?.({
            input: condition.input,
            operator: event.target.value as FieldOperationConditionOperator,
            value: condition.value
          })
        }}
      />

      {!(isEmptyOperator || isErrorOperator || condition.input?.fieldType === 'boolean') && (
        <FieldPropertyMention
          fields={fields}
          fieldProperties={fieldProperties}
          value={{
            strings: condition.value?.template.strings ?? [],
            args: condition.value?.template.args ?? []
          }}
          onChange={value => {
            onChange?.({
              input: condition.input,
              operator: condition.operator,
              value: { type: 'template', template: { strings: value.strings, args: value.args } }
            })
          }}
        />
      )}

      {condition.input?.fieldType === 'boolean' && (
        <Select
          size="md"
          options={[
            { value: '', label: '-', disabled: true },
            { value: 'true', label: t('True') },
            { value: 'false', label: t('False') }
          ]}
          value={condition.value?.template.strings[0] ?? ''}
          onChange={event => {
            onChange?.({
              input: condition.input,
              operator: condition.operator,
              value: { type: 'template', template: { strings: [event.target.value], args: [] } }
            })
          }}
        />
      )}

      <Button
        ghost
        square
        size="sm"
        onClick={() => onDelete?.()}
      >
        <TrashIcon size={16} />
      </Button>
    </>
  )
}
